<!--
  Specialization of ElColorableInput for examples

  In addition to ElColorableInput behavior:

  Emits:
  - @suggestion(val): requests a suggestion
      ex: null // no suggestion requested
      ex: { // suggestion requested
            key: "key", // the segment key
            type: "type", // the type of segment
            query: "text", // the text to suggest from
            input: true, // triggered directly by user input
            x: 123, // the x position on the screen
          }

  Methods:
  - insert(entity, key, start, stop, text):
      inserts a new entity or synonym segment "entity", inside the segment "key"
      from "start" to "stop" with the text "text"
  - unreference(key): replace thesegment "key" by a text segment
  - setName(key, val): changes the entityid or synonymid of the segment "key"
  - setText(key, val): changes the text of the segment "key"
-->

<script>
import XRegExp from 'xregexp'
import ElColorableInput from './ElColorableInput.vue'

const GLOBAL_FIELDS = ['name', 'color']
// to replace an entity whose text has been emptied outside
const PLACEHOLDER = '~'
const LETTERS = '\\p{M}\\p{L}\\p{N}'
const KEEP_LETTER = new XRegExp('^([^L]*)([L](?:.*[L])?)([^L]*)$'.replace(/L/g, LETTERS), 'u')

export default {
  name: 'ElExampleInput',
  extends: ElColorableInput,

  data () {
    return {
      globalFields: GLOBAL_FIELDS,
    }
  },

  methods: {
    // parses entities
    fromExpression (expression) {
      const segs = []
      const reg = /([^{]+)|\{@([0-9a-zA-Z_]*):([^}]+)\}/g
      expression.replace(reg, (_, text, slot, stext) => {
        if (text) {
          segs.push({
            type: 'text',
            text: text,
            key: Math.random() + '',
          })
        } else {
          segs.push({
            type: 'slot',
            name: slot,
            color: 'transparent',
            text: stext.replace(/^~+|~+$/, ''),
            key: Math.random() + '',
          })
        }
        return ''
      })
      return segs
    },
    // back into string
    toExpression (segs) {
      return segs.map((seg) => {
        if (seg.type === 'slot') {
          return '{@' + seg.name + ':' + (seg.text || PLACEHOLDER) + '}'
        } else return seg.text
      }).join('')
    },
    // clean a list of segments, by adding keys and others
    cleanSegments (segs) {
      let out = []
      segs.forEach((seg) => {
        if (!seg) return
        else if (typeof seg === 'string') {
          out.push({
            type: 'text',
            text: seg,
            key: Math.random() + '',
          })
        } else if (seg.type === 'slot') {
          out.push({
            type: seg.type,
            name: seg.name || '',
            color: seg.color || 'transparent',
            text: seg.text || '',
            key: seg.key || Math.random() + '',
          })
        } else if (!seg.text) return
        else {
          out.push({
            type: 'text',
            text: seg.text,
            key: seg.key || Math.random() + '',
          })
        }
      })
      return out
    },
    // check that is still looks like an synonym
    parseSlot (attrs, methods) {
      attrs.text = attrs.text.replace(/^~+|~+$/, '')
      let m = attrs.text.match(KEEP_LETTER)
      if (!m) {
        if (attrs.text) methods.push_text(attrs.text)
        attrs.text = ''
        methods.push_seg(attrs)
      } else {
        if (m[1]) methods.push_text(m[1])
        attrs.text = m[2]
        methods.push_seg(attrs)
        if (m[3]) methods.push_text(m[3])
      }
    },
    // puts PLACEHOLDER if needed
    renderSlot (seg, el, attrs) {
      if (seg.color) el.css('background-color', seg.color)
      return el.text(seg.text || PLACEHOLDER).attr(attrs)
    },

    // inserts an entity in a text segment
    insert (slot, key, start, stop, text) {
      if (key === undefined) key = slot.key
      if (start === undefined) start = slot.start
      if (stop === undefined) stop = slot.stop
      if (text === undefined) text = slot.text
      let out = [], found = false
      this.copySegments.forEach((seg) => {
        if (seg.key === key) {
          found = true
          if (start > 0) {
            out.push({
              type: 'text',
              text: seg.text.substring(0, start),
              key: Math.random() + '',
            })
          }
          out.push({
            type: 'slot',
            text: text.replace(/^~+|~+$/, ''),
            name: slot.name || '',
            color: slot.color || 'transparent',
            key: key,
          })
          if (stop < seg.text.length) {
            out.push({
              type: 'text',
              text: seg.text.substring(stop),
              key: Math.random() + '',
            })
          }
        } else out.push(seg)
      })
      if (found) this.copySegments = out
      else throw new TypeError('key "' + key + '" not found')
    },
    // removes an synonym
    unreference (key) {
      let out = [], found = false, last = null
      this.copySegments.forEach((seg) => {
        if (seg.key === key) {
          found = true
          if (last) {
            last.text += seg.text
          } else {
            last = {
              type: 'text',
              text: seg.text,
              key: Math.random() + '',
            }
            out.push(last)
          }
        } else {
          if (seg.type !== 'text') {
            last = null
            out.push(seg)
          } else if (last) {
            last.text += seg.text
          } else {
            last = seg
            out.push(last)
          }
        }
      })
      if (found) this.copySegments = out
      else throw new TypeError('key "' + key + '" not found')
    },
    // changes the ref of an entity
    setName (key, val) {
      val = val || null
      let found = false
      this.copySegments.forEach((seg) => {
        if (seg.key === key && seg.type !== 'text') {
          if (seg.name === val) found = null
          else {
            found = true
            seg.name = val
          }
        }
      })
      if (found === null) return
      if (!found) throw new TypeError('key "' + key + '" not found')
      // rerender the contenteditable div
      this.renderHTML()
      this.setPosition(this.position.absolute)
      // update history
      this.history.globals[key] = {
        ...this.history.globals[key] || {},
        name: val,
      }
      // emit an event
      this.$emit('update:segments', this.copySegments)
      // update expression
      this.copyExpression = this.toExpression(this.copySegments)
    },
    // changes the ref of an entity
    setColor (key, val) {
      val = val || null
      let found = false
      this.copySegments.forEach((seg) => {
        if (seg.key === key && seg.type !== 'text') {
          if (seg.color === val) found = null
          else {
            found = true
            seg.color = val
          }
        }
      })
      if (found === null) return
      if (!found) throw new TypeError('key "' + key + '" not found')
      // rerender the contenteditable div
      this.renderHTML()
      this.setPosition(this.position.absolute)
      // update history
      this.history.globals[key] = {
        ...this.history.globals[key] || {},
        color: val,
      }
      // emit an event
      this.$emit('update:segments', this.copySegments)
      // update expression
      this.copyExpression = this.toExpression(this.copySegments)
    },
    // changes the ref of an entity
    setColors (colors) {
      let found = false
      this.copySegments.forEach((seg) => {
        if (seg.key in colors && seg.type !== 'text') {
          if (colors[seg.key] && seg.color !== colors[seg.key]) {
            seg.color = colors[seg.key]
            found = true
            // update history
            this.history.globals[seg.key] = {
              ...this.history.globals[seg.key] || {},
              color: colors[seg.key],
            }
          }
        }
      })
      if (!found) return
      // rerender the contenteditable div
      this.renderHTML()
      this.setPosition(this.position.absolute)
      // emit an event
      this.$emit('update:segments', this.copySegments)
      // update expression
      this.copyExpression = this.toExpression(this.copySegments)
    },
    // changes the text of an entity
    setText (key, val) {
      let found = false
      this.copySegments.forEach((seg) => {
        if (seg.key === key && seg.type !== 'text') {
          if (seg.text === val) found = null
          else {
            found = true
            seg.text = val
          }
        }
      })
      if (found === null) return
      if (!found) throw new TypeError('key "' + key + '" not found')
      // merge all text edit together
      this.edit = { key: key, diff: 0, right: -1 }
      // rerender the contenteditable div
      this.renderHTML()
      this.setPosition(this.position.absolute)
      // push the new state
      this.pushHistory(this.copySegments)
      // emit an event
      this.$emit('update:segments', this.copySegments)
      // update expression
      this.copyExpression = this.toExpression(this.copySegments)
    },
  },
}

export function stringify (value) {
  return value.replace(/\{@([0-9a-zA-Z_]*):([^}]+)\}/g, '$2')
}

export function allSlots (value) {
  let slots = []
  const reg = /{@([0-9a-zA-Z_]*):[^}]+\}/g
  value.replace(reg, (_, slot) => {
    slots.push(slot)
    return ''
  })
  return slots
}
</script>
